import { Button } from "@designs-by-maryam/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@designs-by-maryam/ui/components/dialog";
import { Input } from "@designs-by-maryam/ui/components/input";
import { Label } from "@designs-by-maryam/ui/components/label";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import {
	Edit,
	ExternalLink,
	Eye,
	FileText,
	Loader2,
	Plus,
	Send,
	Trash2,
	Undo,
} from "lucide-react";
import { useMemo, useState } from "react";
import { DataTable, SortableHeader } from "@/components/shared/data-table";
import { requireDesigner } from "@/functions/require-role";
import { tryGetPayloadUrl } from "@/lib/payload";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_dashboard/designer/pages")({
	component: PagesManager,
	beforeLoad: async () => {
		await requireDesigner();
	},
});

type Page = {
	id: string;
	slug: string;
	title: string;
	status: "draft" | "published" | "archived";
	created_at: Date;
	updated_at: Date;
	published_at: Date | null;
};

function PagesManager() {
	const queryClient = useQueryClient();
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [newPageTitle, setNewPageTitle] = useState("");
	const [newPageSlug, setNewPageSlug] = useState("");
	const [pageToDelete, setPageToDelete] = useState<Page | null>(null);
	const payloadUrl = tryGetPayloadUrl();
	const payloadAdminUrl = payloadUrl ? `${payloadUrl}/admin` : null;

	// Fetch all pages
	const pagesQuery = useQuery(orpc.listPages.queryOptions());

	// Create page mutation
	const createMutation = useMutation({
		mutationFn: async () =>
			orpc.createPage.call({
				title: newPageTitle,
				slug: newPageSlug,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["pages"] });
			setIsCreateDialogOpen(false);
			setNewPageTitle("");
			setNewPageSlug("");
		},
	});

	// Publish page mutation
	const publishMutation = useMutation({
		mutationFn: async (pageId: string) => orpc.publishPage.call({ id: pageId }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["pages"] });
		},
	});

	// Unpublish page mutation
	const unpublishMutation = useMutation({
		mutationFn: async (pageId: string) =>
			orpc.unpublishPage.call({ id: pageId }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["pages"] });
		},
	});

	// Delete page mutation
	const deleteMutation = useMutation({
		mutationFn: async (pageId: string) => orpc.deletePage.call({ id: pageId }),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["pages"] });
		},
	});

	// Auto-generate slug from title
	const handleTitleChange = (title: string) => {
		setNewPageTitle(title);
		setNewPageSlug(
			title
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, "-")
				.replace(/^-|-$/g, ""),
		);
	};

	// TanStack Table columns
	const columns: ColumnDef<Page>[] = useMemo(
		() => [
			{
				accessorKey: "title",
				header: ({ column }) => (
					<SortableHeader column={column}>Title</SortableHeader>
				),
				cell: ({ row }) => (
					<span className="font-medium">{row.getValue("title")}</span>
				),
			},
			{
				accessorKey: "slug",
				header: ({ column }) => (
					<SortableHeader column={column}>Slug</SortableHeader>
				),
				cell: ({ row }) => (
					<span className="text-muted-foreground">/{row.getValue("slug")}</span>
				),
			},
			{
				accessorKey: "status",
				header: "Status",
				cell: ({ row }) => {
					const status = row.getValue("status");
					const statusStr = (typeof status === "string" && status) || "draft";
					return (
						<span
							className={`inline-flex items-center rounded-full px-2 py-1 font-medium text-xs ${
								statusStr === "published"
									? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
									: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
							}`}
						>
							{statusStr}
						</span>
					);
				},
				filterFn: (row, id, value) => value.includes(row.getValue(id)),
			},
			{
				accessorKey: "updated_at",
				header: ({ column }) => (
					<SortableHeader column={column}>Updated</SortableHeader>
				),
				cell: ({ row }) => {
					const updatedAt = row.getValue("updated_at");
					let date: Date | null = null;
					if (updatedAt instanceof Date) {
						date = updatedAt;
					} else if (typeof updatedAt === "string") {
						date = new Date(updatedAt);
					}
					return (
						<span className="text-muted-foreground text-sm">
							{date?.toLocaleDateString() ?? "—"}
						</span>
					);
				},
			},
			{
				id: "actions",
				header: () => <span className="sr-only">Actions</span>,
				cell: ({ row }) => {
					const page = row.original;
					return (
						<div className="flex items-center justify-end gap-1">
							{/* Edit Button */}
							<Button asChild size="icon" variant="ghost">
								<Link
									params={{ pageId: page.id }}
									to="/designer/editor/$pageId"
								>
									<Edit className="h-4 w-4" />
									<span className="sr-only">Edit {page.title}</span>
								</Link>
							</Button>

							{/* Preview Button */}
							<Button asChild size="icon" variant="ghost">
								<a
									href={`/${page.slug}`}
									rel="noopener noreferrer"
									target="_blank"
								>
									<Eye className="h-4 w-4" />
									<span className="sr-only">Preview {page.title}</span>
								</a>
							</Button>

							{/* Publish/Unpublish Button */}
							{page.status === "draft" ? (
								<Button
									disabled={publishMutation.isPending}
									onClick={() => publishMutation.mutate(page.id)}
									size="icon"
									variant="ghost"
								>
									<Send className="h-4 w-4" />
									<span className="sr-only">Publish {page.title}</span>
								</Button>
							) : (
								<Button
									disabled={unpublishMutation.isPending}
									onClick={() => unpublishMutation.mutate(page.id)}
									size="icon"
									variant="ghost"
								>
									<Undo className="h-4 w-4" />
									<span className="sr-only">Unpublish {page.title}</span>
								</Button>
							)}

							{/* Delete Button */}
							<Button
								disabled={deleteMutation.isPending}
								onClick={() => setPageToDelete(page)}
								size="icon"
								variant="ghost"
							>
								<Trash2 className="h-4 w-4 text-destructive" />
								<span className="sr-only">Delete {page.title}</span>
							</Button>
						</div>
					);
				},
			},
		],
		[publishMutation, unpublishMutation, deleteMutation],
	);

	return (
		<div className="container mx-auto p-6">
			<div className="mb-8 flex items-center justify-between">
				<div>
					<h1 className="flex items-center gap-3 font-bold text-3xl">
						<FileText className="h-8 w-8 text-primary" />
						Page Builder
					</h1>
					<p className="mt-2 text-muted-foreground">
						Create and manage visual page layouts with Puck editor
					</p>
					<p className="mt-1 text-muted-foreground text-sm">
						Structured content (projects, services, testimonials) lives in
						Payload Content Manager.
					</p>
				</div>

				<div className="flex items-center gap-3">
					{payloadAdminUrl && (
						<Button asChild variant="outline">
							<a
								href={payloadAdminUrl}
								rel="noopener noreferrer"
								target="_blank"
							>
								<ExternalLink className="mr-2 h-4 w-4" />
								Open Content Manager
							</a>
						</Button>
					)}

					<Dialog
						onOpenChange={setIsCreateDialogOpen}
						open={isCreateDialogOpen}
					>
						<DialogTrigger asChild>
							<Button>
								<Plus className="mr-2 h-4 w-4" />
								New Page
							</Button>
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Create New Page</DialogTitle>
								<DialogDescription>
									Enter a title and URL slug for the new page. You can edit the
									layout after creation.
								</DialogDescription>
							</DialogHeader>
							<div className="grid gap-4 py-4">
								<div className="grid gap-2">
									<Label htmlFor="title">Page Title</Label>
									<Input
										id="title"
										onChange={(e) => handleTitleChange(e.target.value)}
										placeholder="Homepage"
										value={newPageTitle}
									/>
								</div>
								<div className="grid gap-2">
									<Label htmlFor="slug">URL Slug</Label>
									<Input
										id="slug"
										onChange={(e) => setNewPageSlug(e.target.value)}
										placeholder="homepage"
										value={newPageSlug}
									/>
									<p className="text-muted-foreground text-xs">
										Page will be available at: /{newPageSlug || "slug"}
									</p>
								</div>
							</div>
							<DialogFooter>
								<Button
									disabled={
										!(newPageTitle && newPageSlug) || createMutation.isPending
									}
									onClick={() => createMutation.mutate()}
								>
									{createMutation.isPending ? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											Creating...
										</>
									) : (
										"Create Page"
									)}
								</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				</div>
			</div>

			{/* Loading State */}
			{pagesQuery.isLoading && (
				<div className="flex items-center justify-center py-12">
					<Loader2 className="h-8 w-8 animate-spin text-primary" />
					<span className="ml-2 text-muted-foreground">Loading pages...</span>
				</div>
			)}

			{/* Error State */}
			{pagesQuery.error && (
				<div className="rounded-lg border border-destructive bg-destructive/10 p-6">
					<p className="font-medium text-destructive">Error loading pages</p>
					<p className="mt-1 text-muted-foreground text-sm">
						{pagesQuery.error.message}
					</p>
				</div>
			)}

			{/* Empty State */}
			{pagesQuery.data?.data.length === 0 && (
				<div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
					<FileText className="h-12 w-12 text-muted-foreground" />
					<h3 className="mt-4 font-medium text-lg">No pages yet</h3>
					<p className="mt-1 text-muted-foreground">
						Create your first page to get started
					</p>
					<Button className="mt-4" onClick={() => setIsCreateDialogOpen(true)}>
						<Plus className="mr-2 h-4 w-4" />
						Create Page
					</Button>
				</div>
			)}

			{/* Pages Table with TanStack Table */}
			{pagesQuery.data && pagesQuery.data.data.length > 0 && (
				<DataTable
					caption="List of pages you can manage"
					columns={columns}
					data={pagesQuery.data.data as Page[]}
					searchKey="title"
					searchPlaceholder="Search pages..."
				/>
			)}

			{/* Delete Confirmation Dialog */}
			<Dialog
				onOpenChange={(open) => !open && setPageToDelete(null)}
				open={!!pageToDelete}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Delete Page</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete &quot;{pageToDelete?.title}&quot;?
							This action cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button onClick={() => setPageToDelete(null)} variant="outline">
							Cancel
						</Button>
						<Button
							disabled={deleteMutation.isPending}
							onClick={() => {
								if (pageToDelete) {
									deleteMutation.mutate(pageToDelete.id, {
										onSuccess: () => setPageToDelete(null),
									});
								}
							}}
							variant="destructive"
						>
							{deleteMutation.isPending ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Deleting...
								</>
							) : (
								"Delete"
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
